import { useMutation } from '@apollo/client'
import { LinkResultStatus } from '@api/mesa/graphql'
import { GET_PLAID_LINKED_ACCOUNTS } from '@/src/common/gql/queries/plaid'
import { useLogError } from '@/src/common/hooks/useLogError'
import { COMPLETE_PLAID_LINK } from '@/src/common/gql/mutations/plaid'
import { GET_LINKED_EXTERNAL_ACCOUNTS } from '@/src/common/gql/queries/card'
import { triggerSegmentAction } from '@/src/services/segment'
import { plaidLinkErrorMessage } from '@/src/common/hooks/usePlaidLink/plaidLinkErrorMessage'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { useEvent } from 'tamagui'
import { GET_MESA_HOME_ENROLLMENT } from '../../../gql/queries/mortgage-lead'
import type { LinkSuccessInput } from '@api/mesa/graphql'
import type { UseCompletePlaidLink } from '@/src/common/hooks/usePlaidLink/useCompletePlaidLink/CompletePlaidLinkMutation'

export type LegacyCompletePlaidLinkProps = Pick<LinkSuccessInput, 'shouldImportTransactions'> &
  Pick<LinkSuccessInput['accounts'][number], 'shouldLinkToHighnote'>

export const useLegacyCompletePlaidLink: UseCompletePlaidLink<LegacyCompletePlaidLinkProps> = ({
  shouldLinkToHighnote,
  shouldImportTransactions,
}) => {
  const Alert = useAlert(useLegacyCompletePlaidLink.name)
  const onError = useLogError()

  const [completePlaidLink, { loading }] = useMutation(COMPLETE_PLAID_LINK, {
    refetchQueries: [
      GET_PLAID_LINKED_ACCOUNTS,
      GET_LINKED_EXTERNAL_ACCOUNTS,
      GET_MESA_HOME_ENROLLMENT,
    ],
    onError,
  })

  return [
    useEvent(async ({ publicToken, metadata }, ...context) => {
      const completePlaidLinkResult = await completePlaidLink({
        variables: {
          input: {
            publicToken,
            institution: metadata.institution
              ? { id: metadata.institution.id, name: metadata.institution.name }
              : undefined,
            accounts: metadata.accounts.map((account) => ({
              id: account.id,
              institutionId: metadata.institution?.id,
              mask: account.mask ?? '',
              name: account.name ?? '',
              subtype: String(account.subtype),
              type: account.type,
              shouldLinkToHighnote,
            })),
            shouldImportTransactions,
          },
        },
      })

      const status = completePlaidLinkResult.data?.completePlaidLink.status

      if (!status) {
        void triggerSegmentAction({
          component: 'Plaid Link Event',
          action: 'User Encountered an Error During Plaid Link',
        })
        Alert.alert('Error', plaidLinkErrorMessage)
        onError('no status found from completePlaidLink', completePlaidLinkResult, ...context)
        return
      }

      switch (status) {
        case LinkResultStatus.Success: {
          Alert.alert('Success', 'Account linked successfully')
          break
        }
        case LinkResultStatus.PendingAuthVerification: {
          Alert.alert(
            'Verification Needed',
            'You need to verify your account before it can be used.',
          )
          void triggerSegmentAction({
            component: 'Plaid Link Event',
            action: 'User is Pending Auth Manual Verification',
          })
          break
        }
        case LinkResultStatus.DuplicateAccount: {
          Alert.alert('This account is currently unavailable for linking')
          void triggerSegmentAction({
            component: 'Plaid Link Event',
            action: 'User tried linking an account that was already linked',
          })
          break
        }
        case LinkResultStatus.Error: {
          Alert.alert('Error', plaidLinkErrorMessage)
          void triggerSegmentAction({
            component: 'Plaid Link Event',
            action: 'User Encountered an Error During Plaid Link',
          })
          onError('completePlaidLink encountered Error', ...context)
          break
        }
        default: {
          // if this check fails, we haven't handled every possible `status`
          status satisfies never
          Alert.alert('Error', plaidLinkErrorMessage)
          void triggerSegmentAction({
            component: 'Plaid Link Event',
            action: 'User Encountered an Error During Plaid Link',
          })
          onError('completePlaidLink encountered unexpected status', ...context)
          break
        }
      }
    }),
    { loading },
  ]
}
